import { FC } from 'react';
import { Modal, Alert } from '@mui/material';
import { CloseIcon } from 'assets';
import { Button, Input } from 'view/components';
import * as CS from 'view/modals/styled';
import { useRequest } from 'domain/core/hooks/useRequest';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';
import { showSystemToast } from 'view/components/SystemToast';
import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { useContextSelector as useCS } from 'use-context-selector';
import { GlobalConfigContext } from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { CreateCompanyLogoUpload } from 'view/pages/createJobPost/components/CreateCompanyLogoUpload';

interface RequestCompanyChangesModalProps {
  open: boolean;
  onClose: () => void;
  company: any;
}

interface IFormInput {
  name: string;
  websiteUrl: string;
  descriptionShort: string;
  descriptionLong: string;
  industryRaw: string;
  country: string;
  region: string;
  city: string;
  sizeRange: string;
  linkedinUrl: string;
  fileId: string;
}

const FIELDS: {
  name: keyof IFormInput;
  label: string;
  placeholder: string;
  multiline?: boolean;
  rows?: number;
}[] = [
  { name: 'name', label: 'Company Name', placeholder: 'Enter company name' },
  { name: 'websiteUrl', label: 'Website URL', placeholder: 'https://www.example.com' },
  { name: 'industryRaw', label: 'Industry', placeholder: 'Enter industry' },
  { name: 'country', label: 'Country', placeholder: 'Enter country' },
  { name: 'region', label: 'Region', placeholder: 'Enter region' },
  { name: 'city', label: 'City', placeholder: 'Enter city' },
  { name: 'sizeRange', label: 'Company Size', placeholder: 'e.g., 51-200' },
  { name: 'linkedinUrl', label: 'LinkedIn URL', placeholder: 'https://linkedin.com/company/...' },
  {
    name: 'descriptionShort',
    label: 'Short Description',
    placeholder: 'Brief summary of the company...',
    multiline: true,
    rows: 2,
  },
  {
    name: 'descriptionLong',
    label: 'Long Description',
    placeholder: 'Detailed company profile, culture, and values...',
    multiline: true,
    rows: 4,
  },
];

const buildChanges = (formData: IFormInput, company: any): Record<string, string> => {
  const changes: Record<string, string> = {};

  FIELDS.forEach(({ name }) => {
    const newVal = (formData[name] || '').trim();
    const oldVal = (company[name] || '').trim();
    if (newVal !== oldVal) {
      changes[name] = newVal;
    }
  });

  // Logo change is submitted as the uploaded media logoFileId, compared against the stored logo.
  const newFileId = (formData.fileId || '').trim();
  const oldFileId = (company.logo?.id || '').trim();
  if (newFileId !== oldFileId) {
    changes.logoFileId = newFileId;
  }

  return changes;
};

const ModalFields: FC<{
  onClose: () => void;
  isLoading: boolean;
  error: any;
  company: any;
}> = ({ onClose, isLoading, error, company }) => {
  const { setValue } = useFormContext<IFormInput>();
  const userData: any = useCS(GlobalConfigContext, globalConfigSelectors.userData);

  return (
    <>
      <CS.ModalMainHeader style={{ marginBottom: '24px' }}>
        <CS.ModalMainHeaderTitle>Request Company Changes</CS.ModalMainHeaderTitle>
        <CS.ModalMainHeaderSubTitle>
          Edit the fields you want to change. Only modified fields will be submitted for review.
        </CS.ModalMainHeaderSubTitle>
      </CS.ModalMainHeader>

      <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '10px' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.response?.data?.message || 'Failed to submit change request'}
          </Alert>
        )}

        <CreateCompanyLogoUpload
          currentLink={company?.effectiveLogoUrl || company?.logoUrl || undefined}
          prefix={userData?.id ? `user-${userData.id}` : undefined}
          postfix={company?.name || undefined}
          onUpload={(id) => {
            return setValue('fileId', id);
          }}
          onRemove={() => {
            return setValue('fileId', '');
          }}
        />

        {FIELDS.map((field) => {
          return (
            <Input
              key={field.name}
              name={field.name}
              label={field.label}
              placeholder={field.placeholder}
              multiline={field.multiline}
              rows={field.rows}
              marginbottom={16}
            />
          );
        })}

        <Alert severity="info" sx={{ mb: 2 }}>
          Your changes will be reviewed by an administrator before being applied.
        </Alert>
      </div>

      <CS.ModalMainFooter>
        <Button
          variant="outlined"
          text="Cancel"
          onClick={onClose}
          disabled={isLoading}
          style={{ width: '100%', minHeight: '50px' }}
        />
        <Button
          variant="contained"
          text="Submit Request"
          type="submit"
          disabled={isLoading}
          style={{ width: '100%', minHeight: '50px' }}
        />
      </CS.ModalMainFooter>
    </>
  );
};

export const RequestCompanyChangesModal: FC<RequestCompanyChangesModalProps> = ({
  open,
  onClose,
  company,
}) => {
  const { send, isLoading, error } = useRequest({
    url: '/api/v1/company-change-requests',
    method: ApiMethodsEnum.Post,
  });

  const onSubmit = async (formData: IFormInput) => {
    const changes = buildChanges(formData, company);

    if (Object.keys(changes).length === 0) {
      showSystemToast('info', 'No changes detected.');
      return;
    }

    const result = await send({
      companyId: company.id,
      changes,
    });

    if (result) {
      showSystemToast('success', 'Change request submitted for review.');
      onClose();
    }
  };

  const defaultValues: IFormInput = {
    name: company?.name || '',
    websiteUrl: company?.websiteUrl || '',
    descriptionShort: company?.descriptionShort || '',
    descriptionLong: company?.descriptionLong || '',
    industryRaw: company?.industryRaw || '',
    country: company?.country || '',
    region: company?.region || '',
    city: company?.city || '',
    sizeRange: company?.sizeRange || '',
    linkedinUrl: company?.linkedinUrl || '',
    // Stored logo file id so an untouched logo produces no false diff.
    fileId: company?.logo?.id || '',
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="request-company-changes-modal">
      <CS.ModalMainWrapper style={{ width: '600px', maxWidth: '95%' }}>
        <CS.CloseIconBox onClick={onClose}>
          <CloseIcon />
        </CS.CloseIconBox>
        <FormContainer key={company?.id} defaultValues={defaultValues} onSuccess={onSubmit}>
          <ModalFields onClose={onClose} isLoading={isLoading} error={error} company={company} />
        </FormContainer>
      </CS.ModalMainWrapper>
    </Modal>
  );
};
